import {Box, Paper} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import {SurveyQuestionType} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import React from "react";
import {useTranslation} from "react-i18next";
import {renderTemplateVariables} from "../shared/surveyMarkdownText.util";
import SurveyRunnerQuestionTitle from "./SurveyRunnerQuestionTitle";
import {
    RunnerQuestionServices,
    SurveyAnswerDraft,
    SurveyRunnerLocalize
} from "./questions/runnerQuestion.types";
import SurveyRunnerMarkdown from "./SurveyRunnerMarkdown";
import SurveyRunnerQuestion from "./SurveyRunnerQuestion";
import {surveyQuestionLayout} from "./surveyRunnerColors";
import SurveyTextOn from "./SurveyTextOn";

interface Props {
    question: SurveyQuestion
    localize: SurveyRunnerLocalize
    language: string

    value?: SurveyAnswerDraft
    onAnswerChange?: (draft: SurveyAnswerDraft) => void
    services?: RunnerQuestionServices

    error?: string
    hint?: string
    showFooter?: boolean

    /** 3 on a page that shows a section heading; the title is an h2 otherwise. */
    titleLevel?: 2 | 3
    /** Plain question style only: the thin rule above the question. Callers pass it for every card but the first
     *  on the page; Cards ignores it. */
    separated?: boolean
}

/** A card's padding, and in Plain the space below the rule: the same rhythm in both styles. */
const CARD_PADDING = {xs: 2.5, sm: 3};

/*
    Straight on the page, the fields that would show the page through bring the card surface along, as the
    choice tiles already do. On a card, or on Plain's shared sheet, they sit on that surface anyway. A hovered
    button keeps the theme's tint.
*/
const ON_PAGE_ANSWER_SX = {
    '& .MuiOutlinedInput-root, & .MuiButton-outlined:not(:hover)': {bgcolor: 'background.paper'},
};

function SurveyRunnerQuestionCard({
    question,
    localize,
    language,
    value,
    onAnswerChange,
    services,
    error,
    hint,
    showFooter,
    titleLevel,
    separated,
}: Props) {
    /*
        Scoped to the survey's language, not the app's. Everything else on this card is localized
        through `localize`/`language`; the markers carry the only i18n string here, and a guest
        reading an Italian survey in a German-locale browser must not be told "Erforderlich".
    */
    const {t} = useTranslation(undefined, {lng: language});
    const {plain, textOnPage} = surveyQuestionLayout(useTheme());

    const title = localize(question.title);
    const description = localize(question.description);
    // No help in the guest's language means no button, never an empty panel.
    const help = localize(question.helpText);

    // The checklist already explains its all-items requirement directly above its controls.
    const helperText = error ?? (question.type === SurveyQuestionType.CHECKLIST ? undefined : hint);

    /*
        Sensitive is a staff-side handling note: guests are never told that an answer is handled as sensitive,
        so nothing on this card reads `isSensitive` (see shared/SurveyQuestionMarkers, `guest`).
    */
    const body = (
        <>
            {!!title.trim() && (
                <Box sx={{mb: description.trim() ? 1 : 2.5}}>
                    <SurveyRunnerQuestionTitle
                        title={title}
                        required={!!question.isRequired}
                        t={t}
                        level={titleLevel}
                        help={renderTemplateVariables(help).trim() ? help : undefined}
                    />
                </Box>
            )}

            {!!description.trim() && (
                <Box sx={{mb: 2.5}}>
                    <SurveyRunnerMarkdown markdown={description} fontSize={16} color={'text.secondary'}/>
                </Box>
            )}

            {/* Callers focus the first control in here after a failed Continue, never a button in the title. */}
            <Box data-survey-answer sx={plain && textOnPage ? ON_PAGE_ANSWER_SX : undefined}>
                <SurveyRunnerQuestion
                    question={question}
                    localize={localize}
                    language={language}
                    value={value}
                    onAnswerChange={onAnswerChange}
                    services={services}
                />
            </Box>

            {!!showFooter && (
                <Box sx={{minHeight: 20, mt: 0.5}}>
                    <Typography
                        variant={'body2'}
                        component={'p'}
                        role={error ? 'alert' : undefined}
                        color={error ? 'error.main' : 'text.secondary'}
                    >
                        {helperText ?? ''}
                    </Typography>
                </Box>
            )}
        </>
    );

    /*
        Plain: no box, no fill, no border; the question sits on the page (or the frame's sheet) below a thin rule.
        The preview draws the same rule above its staff-only "Conditional" hint (runner/SurveyRunner.tsx) — change
        one and change this.
    */
    if (plain) {
        return (
            <Box sx={separated ? {borderTop: '1px solid', borderColor: 'divider', pt: CARD_PADDING} : undefined}>
                {body}
            </Box>
        );
    }

    return (
        <SurveyTextOn on={'surface'}>
            <Paper
                elevation={0}
                sx={{
                    p: CARD_PADDING,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                {body}
            </Paper>
        </SurveyTextOn>
    );
}

export default SurveyRunnerQuestionCard;
